import { Box, Grid, GridItem, Flex, Text, IconButton, Image, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, useDisclosure, Checkbox, Tooltip, } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useEffect, useRef, useState } from "react";
import UploadLogo from "../../../assets/images/UploadLogo.svg";
import MyDiv from "./employeeGallery.style";
import { AlertCard, CustomToast } from "../../../components";
import { useFetch } from "../../../hooks/useFetch";
type CardType = "birthday" | "work_anniversary" | "marriage_anniversary";

interface ApiEventGroup {
    event_Name: CardType;
    images: {
        image_Id: number;
        image_Name: string;
        imageUrl: string;
        isDefault: boolean;
    }[];
}

interface GalleryItem {
    image_Id: number;
    image_Name: string;
    imageUrl: string;
    isDefault: boolean;
}

const EmployeeGallery = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [activeCard, setActiveCard] = useState<CardType | null>(null);
    const [images, setImages] = useState<Record<CardType, GalleryItem[]>>({
        birthday: [],
        work_anniversary: [],
        marriage_anniversary: [],
    });
    const [files, setFiles] = useState<File[]>([]);
    const [selectedImage, setSelectedImage] = useState<Partial<Record<CardType, GalleryItem>>>({});
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [isSelectAlertOpen, setIsSelectAlertOpen] = useState(false);
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [deleteImageId, setDeleteImageId] = useState<number | null>(null);
    const [pendingSelect, setPendingSelect] = useState<{ card: CardType; img: GalleryItem; } | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSelectAlertLoading, setIsSelectAlertLoading] = useState(false);
    const [isDeleteAlertLoading, setIsDeleteAlertLoading] = useState(false);

    const hasAnySelectedImage = !!selectedImage.birthday || !!selectedImage.work_anniversary || !!selectedImage.marriage_anniversary;

    const handleUpload = async () => {
        if (!activeCard || files.length === 0) return;
        setIsUploading(true);
        const formData = new FormData();
        files.forEach(f => formData.append("images", f));
        formData.append("event_Name", activeCard);

        const res = await fetchApi("EmployeeGallery", "POST", formData, "", "", true);
        if (res) {
            addToast({ message: res.message, status: "success" });
            setFiles([]);
            if (fileRef.current) fileRef.current.value = "";
            onClose();
            loadGallery();
        }
        setIsUploading(false);
    };

    const openUploadModal = (card: CardType): void => {
        setActiveCard(card);
        onOpen();
    };


    const handleSelect = async (card: CardType, img: GalleryItem) => {
        setIsSelectAlertLoading(true);
        const payload = {
            employee_Image_Id: img.image_Id,
            event_Name: card
        };

        const res = await fetchApi("EmployeeGallery/assign-event", "POST", payload);
        if (res) {
            addToast({ message: "Default image updated", status: "success" });
            loadGallery();
        }
        setIsSelectAlertLoading(false);
    };


    const loadGallery = async () => {
        const res: ApiEventGroup[] | undefined = await fetchApi("EmployeeGallery", "GET");
        if (!res) return;
        const mapped: Record<CardType, GalleryItem[]> = {
            birthday: [],
            work_anniversary: [],
            marriage_anniversary: [],
        };
        res.forEach(ev => {
            mapped[ev.event_Name] = ev.images.map(img => ({
                image_Id: img.image_Id,
                image_Name: img.image_Name,
                imageUrl: img.imageUrl,
                isDefault: img.isDefault,
            }));
            const def = ev.images.find(i => i.isDefault);
            if (def) {
                setSelectedImage(prev => ({
                    ...prev,
                    [ev.event_Name]: {
                        image_Id: def.image_Id,
                        image_Name: def.image_Name,
                        imageUrl: def.imageUrl,
                        isDefault: true,
                    },
                }));
            }
        });
        setImages(mapped);
    };



    useEffect(() => {
        loadGallery();
    }, []);



    const confirmDelete = async () => {
        if (!deleteImageId) return;
        setIsDeleteAlertLoading(true);
        const res = await fetchApi(`EmployeeGallery/${deleteImageId}`, "DELETE");
        if (res) {
            addToast({ message: res.message, status: "success" });
            setSelectedImage(prev => {
                const updated = { ...prev };
                (Object.keys(updated) as CardType[]).forEach(card => {
                    if (updated[card]?.image_Id === deleteImageId) {
                        delete updated[card];
                    }
                });
                return updated;
            });
            loadGallery();
        }
        setDeleteImageId(null);
        setIsDeleteAlertOpen(false);
        setIsDeleteAlertLoading(false);
    };

    const confirmSelect = async () => {
        if (!pendingSelect) return;
        await handleSelect(pendingSelect.card, pendingSelect.img);
        setPendingSelect(null);
        setIsSelectAlertOpen(false);
    };


    const closeUploadModal = () => {
        setFiles([]);
        setActiveCard(null);
        if (fileRef.current) {
            fileRef.current.value = "";
        }
        onClose();
    };

    const handleDeleteRequest = (img: GalleryItem) => {
        setDeleteImageId(img.image_Id);
        setIsDeleteAlertOpen(true);
    };

    const renderCard = (title: string, key: CardType) => {
        const cardImages = images[key];

        return (
            <Grid className="grid_container">
                <GridItem colSpan={12}>
                    <Box className="card" overflowX="scroll"  >
                        <Flex justify="space-between" mb={4}>
                            <Text fontWeight="600">{title}</Text>
                            <Image src={UploadLogo} mb={3} w="20px" cursor="pointer" onClick={() => openUploadModal(key)} />
                        </Flex>

                        {cardImages.length === 0 ? (
                            <Box h="120px" />
                        ) : (
                            <Flex align="center" gap={2}>
                                <Box className="imageRow">
                                    {cardImages.map(img => (
                                        <Box key={img.image_Id} className={`imageBox ${selectedImage[key]?.image_Id === img.image_Id ? "selected" : ""}`} >
                                            {/* <Image src={img.imageUrl} objectFit="contain" /> */}
                                            <Image src={img.imageUrl} />
                                            <Box className="imageHover">
                                                <Tooltip label="This image is already set as default" isDisabled={!img.isDefault} >
                                                    <Box display="inline-flex">
                                                        <Checkbox isChecked={selectedImage[key]?.image_Id === img.image_Id} isDisabled={img.isDefault} onChange={() => {
                                                            if (img.isDefault) return;
                                                            setPendingSelect({ card: key, img });
                                                            setIsSelectAlertOpen(true);
                                                        }}
                                                            colorScheme="green"
                                                            size="lg"
                                                        />
                                                    </Box>
                                                </Tooltip>
                                                <Tooltip label="Default image cannot be deleted" isDisabled={!img.isDefault} >
                                                    <IconButton aria-label="delete" size="xs" icon={<DeleteIcon />} className="deleteBtn" onClick={() => handleDeleteRequest(img)} isDisabled={img.isDefault} />
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Flex>
                        )}
                    </Box>
                </GridItem>
            </Grid>
        );
    };

    return (
        <MyDiv>
            {/* Selected preview */}
            <Box pt={2} mr={4}>
                <Flex justify="space-between" align="center">
                    {hasAnySelectedImage ? (
                        <Text className="font-poppins text_xxl text_semibold" mb={3} > SELECTED EVENT IMAGES</Text>
                    ) :
                        (<Text className="font-poppins text_xxl text_semibold" mb={3} >KINDLY SELECT YOUR  EVENT IMAGES </Text>)
                    }

                    <Tooltip label="Kindly ensure that default images are added for Birthday, Work Anniversary, and Marriage Anniversary email communications."
                        aria-label="Full description" bg="gray.700" color="white" placement="bottom-start" maxW="400px" hasArrow fontSize="lg"
                    >
                        <Button colorScheme="green" size="sm">
                            Help
                        </Button>
                    </Tooltip>

                </Flex>
            </Box>

            <Flex gap={6} mb={6} mt={2}>

                {(["birthday", "work_anniversary", "marriage_anniversary"] as CardType[]).map(card =>
                    selectedImage[card] ? (
                        <Box key={card}>
                            <Text fontWeight="600">{card === "birthday" ? "BIRTHDAY" : card === "work_anniversary" ? "WORK ANNIVERSARY" : "MARRIAGE ANNIVERSARY"}</Text>
                            <Box w="120px" h="120px" borderRadius="6px" overflow="hidden" >
                                <Image src={selectedImage[card]!.imageUrl} w="100%" h="100%" objectFit="contain" />
                            </Box>
                        </Box>
                    ) : null
                )}
            </Flex>

            <Grid templateColumns="1fr" gap={6}>
                <GridItem>{renderCard("Birthday", "birthday")}</GridItem>
                <GridItem>{renderCard("Work Anniversary", "work_anniversary")}</GridItem>
                <GridItem>{renderCard("Wedding Anniversary", "marriage_anniversary")}</GridItem>
            </Grid>


            <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Upload Images</ModalHeader>

                    <ModalBody>
                        {/* Hidden File Input */}
                        <Input type="file" multiple accept="image/*" ref={fileRef} display="none" onChange={(e) => {
                            if (e.target.files) {
                                setFiles(Array.from(e.target.files));
                            }
                        }}
                        />

                        {/* Upload Box */}
                        <Box border="2px dashed" borderColor="gray.300" borderRadius="10px" p={6} textAlign="center" cursor="pointer" transition="all 0.2s" _hover={{ borderColor: "green.500", bg: "gray.50" }} onClick={() => fileRef.current?.click()} >
                            <Text fontSize="md" fontWeight="600" mb={2}>Upload Images</Text>
                            <Text fontSize="sm" color="gray.500">Click here to select multiple images</Text>
                            <Button mt={4} size="sm" bgColor="#F0F5F1" pointerEvents="none" > Choose Files </Button>
                        </Box>
                        {files.length > 0 && (
                            <Text mt={3} textAlign="center" fontSize="sm" color="green.600" fontWeight="500" > {files.length} file{files.length > 1 ? "s" : ""} selected </Text>
                        )}

                        {/* Action Buttons */}
                        <Flex justify="center" gap={3} mt={6}>
                            <Button onClick={closeUploadModal} variant="ghost">
                                Cancel
                            </Button>
                            <Button isLoading={isUploading} colorScheme="green" onClick={handleUpload}>
                                Upload
                            </Button>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <AlertCard isLoading={isSelectAlertLoading} isOpen={isSelectAlertOpen} onClose={() => { setIsSelectAlertOpen(false); setPendingSelect(null); }} onConfirm={confirmSelect} colorScheme="green" title="Set Default Image" description="Are you sure you want to set this image as the default for this event?" />
            <AlertCard isOpen={isDeleteAlertOpen} isLoading={isDeleteAlertLoading} onClose={() => { setIsDeleteAlertOpen(false); setDeleteImageId(null); }} onConfirm={confirmDelete} colorScheme="red" title="Delete Image" description="Are you sure you want to delete this image? This action cannot be undone." />
        </MyDiv>
    );
};

export default EmployeeGallery;


